import { useState } from "react";
import { checkVoucherStatus, claimVoucherPrize, getPrize } from "./data.js";
import { dateLabel } from "./lib/format.js";
import { Search, Ticket } from "lucide-react";

const STATUS_INFO = {
  not_found:        { emoji: "❌", label: "Tidak Ditemukan",                   color: "#dc2626", bg: "#fee2e2" },
  expired:          { emoji: "⏰", label: "Kadaluwarsa",                        color: "#d97706", bg: "#fffbeb" },
  used:             { emoji: "✅", label: "Sudah Diklaim",                      color: "#16a34a", bg: "#f0fdf4" },
  claimed_not_used: { emoji: "🎫", label: "Sudah Diundi — Belum Dipakai",      color: "#7c3aed", bg: "#faf5ff" },
  valid:            { emoji: "🎁", label: "Valid — Siap Diklaim",               color: "#2563eb", bg: "#eff6ff" },
};

export function VoucherClaimPage() {
  const [code, setCode]         = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult]     = useState(null);
  const [prize, setPrize]       = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError]       = useState("");

  async function handleCheck(e) {
    e?.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setChecking(true); setResult(null); setPrize(null); setConfirmed(false); setError("");
    try {
      const res = await checkVoucherStatus(trimmed);
      setResult(res);
      if (res.row?.prize_type) setPrize(getPrize(res.row.prize_type));
    } catch (e) {
      setError(e.message || "Gagal cek voucher.");
    } finally {
      setChecking(false);
    }
  }

  async function handleClaim() {
    const trimmed = code.trim().toUpperCase();
    setSpinning(true); setError("");
    // Jalankan animasi 2 detik sambil tunggu API
    const [p] = await Promise.all([
      claimVoucherPrize(trimmed).catch((e) => { setError(e.message || "Gagal klaim."); return null; }),
      new Promise((r) => setTimeout(r, 2000)),
    ]);
    setSpinning(false);
    if (p) {
      setPrize(p);
      const res = await checkVoucherStatus(trimmed);
      setResult(res);
    }
  }

  function handleConfirmGiven() {
    setConfirmed(true);
  }

  const si = result ? STATUS_INFO[result.status] : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px" }}>
      <div style={{ width: "100%", maxWidth: 480 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🎟️</div>
          <h1 style={{ fontSize: 24, fontWeight: 900, color: "#111827", margin: 0 }}>Klaim Voucher</h1>
          <p style={{ fontSize: 14, color: "#6b7280", marginTop: 6 }}>Masukkan kode voucher survey pasien</p>
        </div>

        {/* Input */}
        <form onSubmit={handleCheck} style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Ticket size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setResult(null); setPrize(null); setError(""); }}
              placeholder="VCR-XXXXXXXX"
              maxLength={12}
              style={{
                width: "100%", padding: "12px 12px 12px 36px",
                border: "2px solid #e5e7eb", borderRadius: 10,
                fontSize: 16, fontFamily: "monospace", fontWeight: 700,
                letterSpacing: 1, boxSizing: "border-box",
              }}
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={checking || !code.trim()}
            style={{
              padding: "0 20px", background: "#1e293b", color: "#fff",
              border: "none", borderRadius: 10, fontWeight: 700,
              fontSize: 14, cursor: checking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <Search size={16} /> {checking ? "Cek…" : "Cek"}
          </button>
        </form>

        {error && (
          <div style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Result Card */}
        {result && si && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 16px rgba(0,0,0,0.08)", overflow: "hidden" }}>

            {/* Status bar */}
            <div style={{ background: si.bg, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 32 }}>{si.emoji}</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: si.color }}>{si.label}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>
                  {result.status === "not_found" && "Kode tidak ditemukan dalam sistem."}
                  {result.status === "expired"   && `Kadaluwarsa sejak ${dateLabel(result.expiredDate, { time: true })}.`}
                  {result.status === "used"      && `Diklaim pada ${dateLabel(result.row?.voucher_used_at, { time: true })}.`}
                  {result.status === "claimed_not_used" && "Hadiah sudah diundi, belum dipakai di pembayaran."}
                  {result.status === "valid"     && "Voucher valid dan siap untuk diundi!"}
                </div>
              </div>
            </div>

            {/* Info klien */}
            {result.row && (
              <div style={{ padding: "16px 20px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>Nama Klien</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{result.row.client_name || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>Rating Survey</div>
                    <div style={{ fontSize: 15, color: "#f59e0b" }}>
                      {"★".repeat(result.row.rating || 0)}{"☆".repeat(5 - (result.row.rating || 0))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>Kode Voucher</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#7c3aed", fontFamily: "monospace", letterSpacing: 1 }}>
                      {result.row.voucher_code}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>Submit Survey</div>
                    <div style={{ fontSize: 13, color: "#374151" }}>{dateLabel(result.row.submitted_at, { time: true })}</div>
                  </div>
                </div>

                {/* Prize display (jika sudah ada) */}
                {prize && (
                  <div style={{
                    background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
                    borderRadius: 12, padding: "18px 20px", color: "#fff", textAlign: "center",
                    animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
                    marginBottom: 16,
                  }}>
                    <style>{`@keyframes popIn{from{transform:scale(0.6);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
                    <div style={{ fontSize: 40, marginBottom: 6 }}>{prize.emoji}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {result.status === "used" || confirmed ? "Hadiah yang Diterima" : "🎉 Selamat! Hadiahmu adalah"}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, margin: "6px 0 4px" }}>{prize.label}</div>
                    <div style={{ fontSize: 13, opacity: 0.85 }}>{prize.desc}</div>
                    {prize.discount > 0 && result.status !== "used" && !confirmed && (
                      <div style={{ marginTop: 12, background: "rgba(255,255,255,0.15)", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                        💡 Terapkan diskon ini di form <strong>Pembayaran</strong> — masukkan kode yang sama
                      </div>
                    )}
                  </div>
                )}

                {/* Tombol Undi */}
                {result.status === "valid" && !prize && !spinning && (
                  <button
                    onClick={handleClaim}
                    style={{
                      width: "100%", padding: "14px",
                      background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
                      color: "#fff", border: "none", borderRadius: 12,
                      fontSize: 16, fontWeight: 800, cursor: "pointer",
                      letterSpacing: "0.02em",
                    }}
                  >
                    🎲 Undi Hadiah Sekarang!
                  </button>
                )}

                {/* Animasi spinning */}
                {spinning && (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
                    <div style={{ fontSize: 56, display: "inline-block", animation: "spin 0.2s linear infinite" }}>🎰</div>
                    <p style={{ color: "#7c3aed", fontWeight: 700, marginTop: 8 }}>Mengundi hadiah…</p>
                  </div>
                )}

                {/* Konfirmasi hadiah non-discount diberikan */}
                {prize && (prize.discount || 0) === 0 && result.status !== "used" && !confirmed && (
                  <button
                    onClick={handleConfirmGiven}
                    style={{
                      width: "100%", padding: "12px",
                      background: "#f0fdf4", color: "#16a34a",
                      border: "2px solid #bbf7d0", borderRadius: 10,
                      fontWeight: 700, fontSize: 14, cursor: "pointer",
                    }}
                  >
                    ✓ Konfirmasi — Hadiah Sudah Diberikan ke Pasien
                  </button>
                )}

                {confirmed && (
                  <div style={{ textAlign: "center", padding: "12px", color: "#16a34a", fontWeight: 700, fontSize: 15 }}>
                    ✅ Selesai! Hadiah sudah diberikan.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
